/**
 * Phase 17.7: CMS languages (English, Arabic) with vue-i18n (composition mode).
 *  - The signed-in person's `language` / `numerals` (Account Settings) win; before sign-in the last choice in this
 *    browser, else the browser's language.
 *  - Arabic is loaded on demand; it sets <html lang="ar" dir="rtl">.
 *  - `uiLocale()` is THE locale for every Intl date/number in the CMS (Arabic-Indic digits by default in Arabic).
 */
import { reactive } from 'vue';
import { createI18n } from 'vue-i18n';
import en, { type Messages } from './en';

export type Lang = 'en' | 'ar';
export type Numerals = 'arab' | 'latn';

/** CLDR Arabic plural categories, in message order: zero | one | two | few | many | other. */
function arabicPlural(n: number, choices: number): number {
  if (choices < 6) return n === 1 ? 0 : Math.min(1, choices - 1);
  const m = n % 100;
  if (n === 0) return 0;
  if (n === 1) return 1;
  if (n === 2) return 2;
  if (m >= 3 && m <= 10) return 3;
  if (m >= 11 && m <= 99) return 4;
  return 5;
}

export const i18n = createI18n({
  legacy: false,
  locale: 'en' as Lang,
  fallbackLocale: 'en',
  // Arabic is added on demand (setLanguage); typed as present so `locale` accepts both.
  messages: { en } as unknown as Record<Lang, Messages>,
  pluralRules: { ar: arabicPlural },
  missingWarn: false,
  fallbackWarn: false,
});

/*
 * Numbers inside messages ({n}, counts, sizes) follow the digits setting: numeric parameters are formatted in
 * the UI locale before interpolation. `t(key, 3)` (plural shorthand) becomes `t(key, { n: '٣' }, 3)`.
 * Patched once on the global composer, which `useI18n()` returns in every component.
 */
{
  const g = i18n.global as unknown as { t: (...a: unknown[]) => string };
  const orig = g.t.bind(g);
  const fmt = (v: unknown) => (typeof v === 'number' ? new Intl.NumberFormat(uiLocale(), { useGrouping: false }).format(v) : v);
  g.t = (key: unknown, a?: unknown, b?: unknown) => {
    if (ui.lang === 'en') return b !== undefined ? orig(key, a, b) : a !== undefined ? orig(key, a) : orig(key);
    if (typeof a === 'number') return orig(key, { n: fmt(a), count: fmt(a) }, a);
    if (a && typeof a === 'object' && !Array.isArray(a)) {
      const named = Object.fromEntries(Object.entries(a).map(([k, v]) => [k, fmt(v)]));
      return b !== undefined ? orig(key, named, b) : orig(key, named);
    }
    return b !== undefined ? orig(key, a, b) : a !== undefined ? orig(key, a) : orig(key);
  };
}

const LANG_KEY = 'codot:lang';
const NUM_KEY = 'codot:numerals';
const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* private mode: not remembered */
  }
};

export const ui = reactive<{ lang: Lang; numerals: Numerals }>({ lang: 'en', numerals: 'arab' });

/** Every call bumps this; a slower earlier call (still loading Arabic) must not override a later one. */
let requestSeq = 0;
export async function setLanguage(lang: Lang, numerals: Numerals = ui.numerals): Promise<void> {
  const seq = ++requestSeq;
  if (lang === 'ar' && !i18n.global.availableLocales.includes('ar')) {
    i18n.global.setLocaleMessage('ar', (await import('./ar')).default);
    await import('@fontsource/noto-sans-arabic/arabic-400.css');
    await import('@fontsource/noto-sans-arabic/arabic-500.css');
    await import('@fontsource/noto-sans-arabic/arabic-600.css');
  }
  if (seq !== requestSeq) return; // a newer choice (e.g. the signed-in person's) already applied
  ui.lang = lang;
  ui.numerals = numerals;
  i18n.global.locale.value = lang;
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  write(LANG_KEY, lang);
  write(NUM_KEY, numerals);
}

/** Before sign-in: this browser's last choice, else its language. */
export function initialLanguage(): Promise<void> {
  const saved = read(LANG_KEY);
  const lang: Lang = saved === 'ar' || saved === 'en' ? saved : navigator.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const num = read(NUM_KEY) === 'latn' ? 'latn' : 'arab';
  return setLanguage(lang, num);
}

/** Locale for Intl (dates, numbers, sizes). English keeps the browser's own conventions. */
export function uiLocale(): string | undefined {
  return ui.lang === 'ar' ? `ar-u-nu-${ui.numerals}` : undefined;
}

/** Plain-TS access (stores, utils): `t('key')`. Components use `useI18n()` or `$t`. */
export const t = (key: string, named?: Record<string, unknown> | number, plural?: number): string =>
  typeof named === 'number' ? i18n.global.t(key, named) : plural !== undefined ? i18n.global.t(key, named ?? {}, plural) : i18n.global.t(key, named ?? {});

/** Numbers in the UI locale (Arabic-Indic digits in Arabic unless switched off). */
export const fmtNumber = (n: number, opts?: Intl.NumberFormatOptions): string => new Intl.NumberFormat(uiLocale(), opts).format(n);
